/**
 * SummaryContractDetails Component
 * Read-only view of contract details within the Contract Summary step
 * Uses table-based layout for clean, even spacing
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Link as MuiLink,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';
import { format } from 'date-fns';
import { formatNumber } from '../../../../../utils/validation';
import { apiPut } from '../../../../../utils/apiClient';
import { useTenant } from '../../../../../contexts/TenantContext';
import ContractInvestorsSection from '../../../ContractInvestorsSection';

function SummaryContractDetails({
  contract,
  summaryData,
}) {
  const navigate = useNavigate();
  const { getStakeholderLabel } = useTenant();
  const issuerLabel = getStakeholderLabel('stakeholder.issuer.label', 'Issuer');
  const investors = summaryData?.investors || [];

  // Local state for likelihood to roll (for immediate UI update)
  const [likelihoodToRoll, setLikelihoodToRoll] = useState(contract.likelihood_to_roll || '');

  // Sync local state when contract prop changes
  useEffect(() => {
    setLikelihoodToRoll(contract.likelihood_to_roll || '');
  }, [contract.likelihood_to_roll]);

  const handleLikelihoodChange = async (newValue) => {
    const previousValue = likelihoodToRoll;
    // Optimistically update the UI
    setLikelihoodToRoll(newValue);

    try {
      await apiPut(`/contracts/${contract.id}/likelihood-to-roll`, {
        likelihood_to_roll: newValue || null
      });
    } catch (err) {
      console.error('Error updating likelihood to roll:', err);
      // Revert on error
      setLikelihoodToRoll(previousValue);
      alert('Failed to update likelihood to roll');
    }
  };

  // Summary row component for consistent table formatting
  const SummaryRow = ({ label, value, highlight = false }) => (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={{ fontWeight: 'medium', width: '40%', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {label}
      </TableCell>
      <TableCell sx={{ fontWeight: highlight ? 'bold' : 'normal', borderBottom: '1px solid', borderColor: 'divider' }}>
        {value || '-'}
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Contract Summary
      </Typography>
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Basic Information
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                <SummaryRow
                  label="Investor"
                  value={
                    contract.is_multi_investor
                      ? <Chip label={`Multiple Investors (${investors.length})`} size="small" color="info" />
                      : contract.investor_name
                  }
                />
                <SummaryRow
                  label="Product Type"
                  value={<Chip label={contract.product_type} size="small" />}
                />
                <SummaryRow
                  label="Capital Amount"
                  value={`${contract.currency_code} ${formatNumber(contract.capital_amount)}`}
                  highlight
                />
                {parseFloat(contract.rolled_funds_amount || 0) > 0 && (
                  <>
                    <SummaryRow
                      label="New Funds"
                      value={`${contract.currency_code} ${formatNumber(contract.new_funds_amount)}`}
                    />
                    <SummaryRow
                      label="Rolled Funds"
                      value={`${contract.currency_code} ${formatNumber(contract.rolled_funds_amount)}`}
                    />
                  </>
                )}
                {contract.user_reference && (
                  <SummaryRow label="External Reference" value={contract.user_reference} />
                )}
                {contract.rolled_from_reference && !contract.is_multi_investor && (
                  <SummaryRow
                    label="Rolled From"
                    value={
                      <MuiLink
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/contracts/${contract.rolled_from_contract_id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {contract.rolled_from_reference}
                        {contract.rolled_from_user_reference ? ` (${contract.rolled_from_user_reference})` : ''}
                      </MuiLink>
                    }
                  />
                )}
                {contract.rolled_into_contracts && contract.rolled_into_contracts.length > 0 && (
                  <SummaryRow
                    label={`Rolled Into${contract.rolled_into_contracts.length > 1 ? ` (${contract.rolled_into_contracts.length})` : ''}`}
                    value={
                      <Box>
                        {contract.rolled_into_contracts.map((child) => (
                          <MuiLink
                            key={child.id}
                            component="button"
                            variant="body2"
                            onClick={() => navigate(`/contracts/${child.id}`)}
                            sx={{ cursor: 'pointer', display: 'block' }}
                          >
                            {child.reference}
                            {child.user_reference ? ` (${child.user_reference})` : ''}
                          </MuiLink>
                        ))}
                      </Box>
                    }
                  />
                )}
                <SummaryRow label="Currency" value={contract.currency_code} />
                <SummaryRow label="Likelihood to Roll" value={contract.is_multi_investor ? 'Set per investor' : (likelihoodToRoll || 'Not specified')} />
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Investment Term & Dates */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Investment Term & Dates
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                <SummaryRow
                  label="Investment Term"
                  value={`${contract.investment_term_value} ${contract.investment_term}`}
                />
                <SummaryRow
                  label="Start Date"
                  value={contract.deposit_date ? format(new Date(contract.deposit_date), 'yyyy-MM-dd') : '-'}
                />
                <SummaryRow
                  label="Maturity Date"
                  value={contract.maturity_date ? format(new Date(contract.maturity_date), 'yyyy-MM-dd') : '-'}
                  highlight
                />
                <SummaryRow
                  label="Funds Received"
                  value={contract.funds_received_date
                    ? format(new Date(contract.funds_received_date), 'yyyy-MM-dd')
                    : 'Same as start date'
                  }
                />
                <SummaryRow
                  label="First Payment Date"
                  value={contract.first_payment_date
                    ? format(new Date(contract.first_payment_date), 'yyyy-MM-dd')
                    : 'Same as start date'
                  }
                />
                <SummaryRow
                  label="Eligible for Re-pricing"
                  value={contract.eligible_for_repricing
                    ? `Yes (${contract.repricing_frequency || '—'})`
                    : 'No'
                  }
                />
                {contract.eligible_for_repricing && (
                  <SummaryRow
                    label="Next Re-pricing Date"
                    value={contract.next_repricing_date
                      ? format(new Date(contract.next_repricing_date), 'yyyy-MM-dd')
                      : '—'
                    }
                  />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Investors Section - for multi-investor contracts */}
        {contract.is_multi_investor && investors.length > 0 && (
          <Grid size={12}>
            <ContractInvestorsSection
              contractId={contract.id}
              investors={investors}
              disabled={true}
              currencyCode={contract.currency_code}
              rolledFromContractId={contract.rolled_from_contract_id}
              rolledFromReference={contract.rolled_from_reference}
              rolledFromUserReference={contract.rolled_from_user_reference}
            />
          </Grid>
        )}

        {/* Interest & Payments */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Interest & Payments
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                <SummaryRow label="Rate Type" value={contract.rate_type} />
                <SummaryRow
                  label="Interest Rate"
                  value={`${contract.interest_rate}%`}
                  highlight
                />
                {contract.rate_type === 'Variable' && (
                  <>
                    {contract.initial_interest_rate != null &&
                      parseFloat(contract.initial_interest_rate) !== parseFloat(contract.interest_rate) && (
                        <SummaryRow
                          label="Initial Interest Rate"
                          value={`${parseFloat(contract.initial_interest_rate).toFixed(2)}%`}
                        />
                      )}
                    {contract.benchmark_rate_type && (
                      <SummaryRow
                        label="Benchmark Rate"
                        value={`${contract.benchmark_rate_type}${
                          contract.benchmark_rate_value != null
                            ? ` (${parseFloat(contract.benchmark_rate_value).toFixed(2)}%)`
                            : ''
                        }`}
                      />
                    )}
                    {contract.rate_spread != null && parseFloat(contract.rate_spread) !== 0 && (
                      <SummaryRow
                        label="Rate Spread"
                        value={`${parseFloat(contract.rate_spread) >= 0 ? '+' : ''}${parseFloat(contract.rate_spread).toFixed(2)}%`}
                      />
                    )}
                    <SummaryRow
                      label="Rate Change Effective"
                      value={
                        contract.rate_change_effective_basis === 'anniversary'
                          ? 'On Annual Anniversary'
                          : 'On Date of Change'
                      }
                    />
                  </>
                )}
                <SummaryRow
                  label="Day Count Basis"
                  value={contract.day_count_basis ? `${contract.day_count_basis} days` : '365 days'}
                />
                <SummaryRow label="Calculation Type" value={contract.calculation_profile_name || contract.calculation_profile_interest_method || contract.interest_calc_type} />
                <SummaryRow label="Interest Frequency" value={contract.interest_paid_frequency} />
                <SummaryRow label="Capital Frequency" value={contract.capital_paid_frequency || 'At Maturity'} />
                {contract.capitalization_frequency && (
                  <SummaryRow label="Capitalization Frequency" value={contract.capitalization_frequency} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Broker & Collateral */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Broker & Collateral
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                <SummaryRow
                  label="Brokered"
                  value={
                    contract.is_brokered
                      ? <Chip label="Yes" size="small" color="primary" />
                      : 'No'
                  }
                />
                {contract.is_brokered && (
                  <>
                    <SummaryRow label="Broker" value={contract.broker_name} />
                    <SummaryRow
                      label="Broker Fees"
                      value={`${contract.broker_fees}% (${contract.currency_code} ${formatNumber((contract.capital_amount * contract.broker_fees) / 100)})`}
                    />
                    {contract.stamp_duty && contract.stamp_duty_fees && (
                      <SummaryRow
                        label="Stamp Duty"
                        value={`${contract.stamp_duty_fees}% (${contract.currency_code} ${formatNumber((contract.capital_amount * contract.stamp_duty_fees) / 100)})`}
                      />
                    )}
                  </>
                )}
                {contract.issuer_id && (
                  <SummaryRow label={issuerLabel} value={contract.issuer_name || '—'} />
                )}
                <SummaryRow
                  label="Has Collateral"
                  value={
                    contract.has_collateral
                      ? <Chip label="Yes" size="small" color="secondary" />
                      : 'No'
                  }
                />
                {contract.has_collateral && (
                  <>
                    <SummaryRow label="Provider" value={contract.collateral_provider_name} />
                    <SummaryRow label="Type" value={contract.collateral_type} />
                    <SummaryRow
                      label="Amount"
                      value={`${contract.currency_code} ${formatNumber(contract.collateral_amount)}`}
                    />
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Rollover Likelihood - Editable (single-investor only; multi-investor LTR is per-investor in the investors table) */}
        {!contract.is_multi_investor && (
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Rollover Likelihood
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: 'medium', width: '40%', borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      Likelihood to Roll
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                      <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                        <Select
                          value={likelihoodToRoll}
                          displayEmpty
                          onChange={(e) => handleLikelihoodChange(e.target.value)}
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="Likely">
                            <Chip label="Likely" size="small" color="success" sx={{ mr: 1 }} /> Likely
                          </MenuItem>
                          <MenuItem value="Middle">
                            <Chip label="Possible" size="small" color="warning" sx={{ mr: 1 }} /> Possible
                          </MenuItem>
                          <MenuItem value="Not likely">
                            <Chip label="Not likely" size="small" color="error" sx={{ mr: 1 }} /> Not likely
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {/* Audit Trail */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Audit Trail
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                <SummaryRow
                  label="Created By"
                  value={
                    contract.created_by ? (
                      <>
                        {contract.created_by}
                        {contract.created_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {format(new Date(contract.created_at), 'dd MMM yyyy HH:mm')}
                          </Typography>
                        )}
                      </>
                    ) : '-'
                  }
                />
                <SummaryRow
                  label="Approved By"
                  value={
                    contract.approved_by ? (
                      <>
                        {contract.approved_by}
                        {contract.approved_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {format(new Date(contract.approved_at), 'dd MMM yyyy HH:mm')}
                          </Typography>
                        )}
                      </>
                    ) : '-'
                  }
                />
                {contract.updated_by && (
                  <SummaryRow
                    label="Last Updated By"
                    value={
                      <>
                        {contract.updated_by}
                        {contract.updated_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {format(new Date(contract.updated_at), 'dd MMM yyyy HH:mm')}
                          </Typography>
                        )}
                      </>
                    }
                  />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SummaryContractDetails;
